import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { MarkdownEditor } from "../components/MarkdownEditor";
import type { GardenRow, LocationRow, PlantRow } from "@garden/types/db";
import { supabase } from "../auth";
import { getPlant, updatePlant, listPlants } from "../db/plants";
import { getLocation, listLocations } from "../db/locations";
import { getGardenById } from "../db/gardens";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import { GardenMap } from "../components/GardenMap";
import { PlantPhotosTab } from "../components/PlantPhotosTab";
import { PlantObservationsTab } from "../components/PlantObservationsTab";
import { PlantAttributesTab } from "../components/PlantAttributesTab";
import { PlantTasksTab } from "../components/PlantTasksTab";
import { PlantEventLogTab } from "../components/PlantEventLogTab";
import { parseViewport, mapLocations, computePins } from "../lib/mapData";
import { useIsMobile } from "../lib/useIsMobile";
import "./PlantDetail.css";

const SYMBOL_HUE: Record<string, string> = {
  rose: "var(--rose-faded)",
  shrub: "var(--sage)",
  shrub_topiary: "var(--sage)",
  bamboo: "var(--sage)",
  tree: "var(--moss-light)",
  tree_conifer: "var(--moss)",
  tree_fruit: "var(--moss-light)",
  veg: "var(--sage)",
  carrot: "var(--terracotta-soft)",
  tomato: "var(--terracotta-soft)",
  herb: "var(--sage-pale)",
  vine: "var(--sage-pale)",
  climber: "var(--sage-pale)",
  daisy: "var(--cream-deep)",
  tulip: "var(--terracotta-soft)",
  sunflower: "var(--honey)",
  lavender: "var(--rose-faded)",
  foxglove: "var(--rose-faded)",
  fern: "var(--moss-light)",
  container: "var(--clay)",
};

type Tab = "notes" | "attributes" | "photos" | "observations" | "tasks" | "log";

const TABS: { value: Tab; label: string; icon: () => React.ReactNode }[] = [
  {
    value: "notes",
    label: "Notes",
    icon: () => (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 3h8l2 2v8H3V3z" />
        <path d="M5 7h6m-6 2.5h6m-6 2.5h4" />
      </svg>
    ),
  },
  {
    value: "attributes",
    label: "Attributes",
    icon: () => (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="4" cy="4.5" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="4" cy="8" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="4" cy="11.5" r="0.9" fill="currentColor" stroke="none" />
        <path d="M7 4.5h6M7 8h6M7 11.5h4" />
      </svg>
    ),
  },
  {
    value: "photos",
    label: "Photos",
    icon: () => (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="8" cy="9" r="3.2" />
        <path d="M3 6h2l1.5-2h3L11 6h2v8H3V6z" />
      </svg>
    ),
  },
  {
    value: "observations",
    label: "Obs.",
    icon: () => (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 4l1.2-1h7.6L13 4v9l-1.2 1H4.2L3 13V4z" />
        <path d="M3 4l5 3 5-3" />
      </svg>
    ),
  },
  {
    value: "tasks",
    label: "Tasks",
    icon: () => (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="10" height="10" rx="1.5" />
        <path d="M5.5 8.2l1.7 1.7 3.3-3.6" />
      </svg>
    ),
  },
  {
    value: "log",
    label: "Log",
    icon: () => (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="8" cy="8" r="5.5" />
        <path d="M8 4.5V8l2.4 1.8" />
      </svg>
    ),
  },
];

export function PlantDetail() {
  const isMobile = useIsMobile();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const fromState = (routerLocation.state as { from?: string } | null)?.from;
  const closeTarget = fromState === "/map" ? "/map" : "/plants";
  const [plant, setPlant] = useState<PlantRow | null>(null);
  const [location, setLocation] = useState<LocationRow | null>(null);
  const [tab, setTab] = useState<Tab>("notes");
  const [error, setError] = useState<string | null>(null);
  const [mapData, setMapData] = useState<{
    garden: GardenRow;
    locations: LocationRow[];
    plants: PlantRow[];
  } | null>(null);

  useEffect(() => {
    if (!supabase || !id) return;
    let active = true;
    (async () => {
      try {
        const p = await getPlant(supabase!, id);
        if (!active) return;
        setPlant(p);
        if (p.location_id) {
          const l = await getLocation(supabase!, p.location_id);
          if (!active) return;
          setLocation(l);
        } else {
          setLocation(null);
        }
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Could not load plant.");
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const gardenId = useCurrentGardenId();
  useEffect(() => {
    if (!supabase || !gardenId) return;
    let active = true;
    Promise.all([
      getGardenById(supabase, gardenId),
      listLocations(supabase, gardenId),
      listPlants(supabase, gardenId),
    ])
      .then(([garden, locations, plants]) => {
        if (!active) return;
        if (!garden) return;
        setMapData({ garden, locations, plants });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [gardenId]);

  const mappedLocations = useMemo(() => mapData ? mapLocations(mapData.locations) : [], [mapData]);
  const pins = useMemo(() => mapData ? computePins(mapData.plants, mappedLocations) : [], [mapData, mappedLocations]);

  if (error) return <div className="PlantDetail__error">{error}</div>;
  if (!plant) return <div className="PlantDetail__loading">Loading…</div>;

  const onNotesSave = async (next: string) => {
    if (!supabase) throw new Error("Supabase not configured.");
    const previous = plant;
    setPlant({ ...plant, notes: next });
    try {
      const updated = await updatePlant(supabase, plant.id, { notes: next });
      setPlant(updated);
    } catch (err) {
      setPlant(previous);
      throw err;
    }
  };

  const heroColor = (plant.symbol && SYMBOL_HUE[plant.symbol]) ?? "var(--sage)";

  return (
    <div className={`PlantDetail${isMobile ? " PlantDetail--mobile" : ""}`}>
      {!isMobile && (
        <div className="PlantDetail__map">
          {mapData ? (
            <GardenMap
              garden={{ id: mapData.garden.id, viewport: parseViewport(mapData.garden.viewport) }}
              locations={mappedLocations}
              pins={pins}
              selectedPinId={plant.id}
              onPinClick={(id) => navigate(`/plants/${id}`, { state: { from: closeTarget } })}
              onMapClick={() => navigate("/map")}
            />
          ) : null}
        </div>
      )}

      <div className="PlantDetail__panel">
        {isMobile && (
          <div
            className="PlantDetail__hero"
            style={{ background: heroColor }}
            aria-hidden="true"
          >
            <button
              type="button"
              className="PlantDetail__heroClose"
              onClick={() => navigate(closeTarget)}
              aria-label={closeTarget === "/map" ? "Back to map" : "Back to plants"}
            >
              ✕
            </button>
          </div>
        )}

        {!isMobile && (
          <div className="PlantDetail__panelHeader">
            <div className="PlantDetail__crumbs">
              <Link to="/plants">Plants</Link>
              <span>›</span>
              <em>{plant.common_name}</em>
            </div>
            <button
              type="button"
              className="PlantDetail__close"
              onClick={() => navigate(closeTarget)}
              aria-label={closeTarget === "/map" ? "Back to map" : "Back to plants"}
            >
              ✕
            </button>
          </div>
        )}

        <div className="PlantDetail__heading">
          <h1>
            {plant.common_name}
            {plant.cultivar && (
              <span className="PlantDetail__cultivar"> '{plant.cultivar}'</span>
            )}
          </h1>
          {plant.scientific_name && (
            <p className="PlantDetail__sci">{plant.scientific_name}</p>
          )}
          <div className="PlantDetail__meta">
            <span className={`PlantDetail__status PlantDetail__status--${plant.status}`}>
              {plant.status.replace(/_/g, " ")}
            </span>
            {location && <span>· {location.name}</span>}
            {plant.planted_date && <span>· planted {plant.planted_date}</span>}
            {plant.source_acquisition && <span>· {plant.source_acquisition}</span>}
            <Link className="PlantDetail__edit" to={`/plants/${plant.id}/edit`}>
              Edit
            </Link>
          </div>
        </div>

        <div className="PlantDetail__tabs" role="tablist">
          {TABS.map(({ value, label, icon }) => (
            <button
              key={value}
              role="tab"
              aria-selected={tab === value}
              className={`PlantDetail__tab ${tab === value ? "PlantDetail__tab--active" : ""}`}
              onClick={() => setTab(value)}
            >
              <span className="PlantDetail__tabIcon" aria-hidden="true">
                {icon()}
              </span>
              <span className="PlantDetail__tabLabel">{label}</span>
            </button>
          ))}
        </div>

        <div className="PlantDetail__tabBody">
          {tab === "notes" ? (
            <NotesTab
              key={plant.id}
              plantId={plant.id}
              initial={plant.notes ?? ""}
              onSave={onNotesSave}
            />
          ) : tab === "photos" ? (
            <PlantPhotosTab key={plant.id} plantId={plant.id} />
          ) : tab === "observations" ? (
            <PlantObservationsTab key={plant.id} plantId={plant.id} />
          ) : tab === "attributes" ? (
            <PlantAttributesTab key={plant.id} plantId={plant.id} />
          ) : tab === "tasks" ? (
            <PlantTasksTab key={plant.id} plantId={plant.id} />
          ) : (
            <PlantEventLogTab key={plant.id} plantId={plant.id} />
          )}
        </div>
      </div>
    </div>
  );
}

interface NotesTabProps {
  plantId: string;
  initial: string;
  onSave: (next: string) => Promise<void>;
}

function NotesTab({ plantId, initial, onSave }: NotesTabProps) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const dirty = value !== initial;

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
    } catch (err) {
      setValue(initial);
      setError(err instanceof Error ? err.message : "Could not save notes.");
    } finally {
      setSaving(false);
    }
  };

  // Per the plan: this is *not* full FTS — the inline filter is just an
  // includes-match used to highlight matches in the rendered preview. Cheap.
  const matchCount = useMemo(() => {
    if (!filter.trim()) return 0;
    const re = new RegExp(escapeRegex(filter.trim()), "gi");
    return (value.match(re) ?? []).length;
  }, [value, filter]);

  return (
    <div className="NotesTab">
      <div className="NotesTab__searchHint NotesTab__searchHint--input">
        <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="6" cy="6" r="4" />
          <path d="M9 9l3 3" />
        </svg>
        <input
          type="text"
          value={filter}
          placeholder="Search within notes — try 'prune', 'feed'…"
          onChange={(e) => setFilter(e.target.value)}
          className="NotesTab__searchInput"
        />
        {filter.trim() && (
          <span className="NotesTab__searchCount">
            {matchCount === 0 ? "no matches" : `${matchCount} match${matchCount === 1 ? "" : "es"}`}
          </span>
        )}
      </div>

      <MarkdownEditor
        value={value}
        onChange={setValue}
        placeholder="Anything you want to remember about this plant…"
        startInPreview
        minHeight={220}
        highlight={filter}
      />

      {error && <p className="NotesTab__error">{error}</p>}
      <div className="NotesTab__actions">
        <Link
          to={`/plants/${plantId}/care-notes`}
          className="NotesTab__careLink"
        >
          + Paste care notes from a source…
        </Link>
        <button
          type="button"
          className="PlantForm__btn PlantForm__btn--primary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : dirty ? "Save notes" : "Saved"}
        </button>
      </div>
    </div>
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

